/* eslint-disable func-style */
/* eslint-disable max-statements */
/* eslint-disable max-lines */
import { Color, MeshStandardMaterial, Vector2 } from 'three';

import type { KitchenSceneBuilder } from '../kitchen-scene-builder.class';
import type { KitchenBlueprintInput } from '../types';
import { type TextureInfo, TextureLoadPriority } from '../../../../model/texture-info';
import { TextureType } from '../../../../model/enums';
import { DESIGN_PACKAGE_ENV_SKY_KEY, DESIGN_PACKAGE_LOOK } from '../../../../constants/design-package-look';
import { SKY_TEXTURES, type SkyResolutionVariants } from '../../../../constants/texture-options';
import { getEffectiveTextureQuality, getSkyResolutionForQuality } from '../../../../hooks/use-three-kitchen/quality';
import type { ModelRequirement, SceneTextureRequirements } from './analyze-texture-requirements.cfn';
import { VALID_COUNTERTOP_KEYS, VALID_SURROUND_KEYS } from '../helpers';
import { getReachableApplianceTextureKeys } from '../../../managers/model-manager/types';

/**
 * Result of selecting sky textures for a given quality level.
 * Returns both the default texture (for immediate display) and the target texture
 * (for async upgrade if different from default).
 */
interface SkyTextureSelection {
	/** Default/2k texture to load immediately */
	defaultTexture: string;
	/** Target texture based on quality (may be same as default or higher res) */
	targetTexture: string;
	/** Whether an async upgrade is needed (target != default) */
	needsUpgrade: boolean;
}

/**
 * Select a KTX2 variant URL for a sky based on render quality. Returns undefined
 * when no KTX2 assets are configured for this sky. When populated, the returned
 * URL is assigned to `TextureInfo.ktx2Path`, which the texture-manager's loader
 * prefers over the plain texture if the device supports KTX2.
 */
const selectKtx2SkyTexture = (
	ktx2Textures: SkyResolutionVariants | undefined,
	ktx2Default: string | undefined,
	renderQuality: number
): string | undefined => {
	if (!ktx2Default && !ktx2Textures) return undefined;
	const resolutionChain = getSkyResolutionForQuality(renderQuality);
	if (resolutionChain.length === 0) return undefined;
	if (!ktx2Textures) return ktx2Default;
	for (const resolution of resolutionChain) {
		if (resolution === 'default') return ktx2Default;
		const file = ktx2Textures[resolution as keyof SkyResolutionVariants];
		if (file) return file;
	}
	return ktx2Default;
};

/**
 * Select both the default and target texture filenames for a sky based on render quality.
 * Always returns the default (2k) texture for immediate loading, plus the target
 * texture for async upgrade if quality requires higher resolution.
 *
 * @param textures - Available resolution variants for this sky
 * @param defaultTexture - Default texture filename (2k fallback)
 * @param renderQuality - Current render quality level (0-10)
 * @returns Selection with default, target, and upgrade flag
 */
const selectSkyTexturesForQuality = (
	textures: SkyResolutionVariants | undefined,
	defaultTexture: string,
	renderQuality: number
): SkyTextureSelection => {
	// Get the resolution fallback chain for this quality level
	const resolutionChain = getSkyResolutionForQuality(renderQuality);

	// No texture for quality 0-1
	if (resolutionChain.length === 0) {
		return { defaultTexture: '', targetTexture: '', needsUpgrade: false };
	}

	// If no resolution variants defined, use default for both
	if (!textures) {
		return { defaultTexture, targetTexture: defaultTexture, needsUpgrade: false };
	}

	// Find the first available resolution in the chain (this is the target)
	let targetTexture = defaultTexture;
	for (const resolution of resolutionChain) {
		if (resolution === 'default') {
			targetTexture = defaultTexture;
			break;
		}
		const textureFile = textures[resolution as keyof SkyResolutionVariants];
		if (textureFile) {
			targetTexture = textureFile;
			break;
		}
	}

	// Check if target is different from default (upgrade needed)
	const needsUpgrade = targetTexture !== defaultTexture;

	return { defaultTexture, targetTexture, needsUpgrade };
};

/**
 * Load all kitchen textures (legacy non-progressive path).
 * Uses the shared *_TEXTURE_DEFS and create*TextureInfo() factory functions
 * as the single source of truth for texture definitions.
 */
export const loadTexturesCfn = async (
	self: KitchenSceneBuilder,
	{ input }: { input: KitchenBlueprintInput }
): Promise<void> => {
	const textures: TextureInfo[] = [];
	const { renderQuality, textureQuality } = self.configAccess;
	const effectiveTextureQuality = getEffectiveTextureQuality(textureQuality, renderQuality);

	const registerAndCollect = (info: TextureInfo): void => {
		self.textureInfoManagerAccess.addTexture(info.key, info);
		textures.push(info);
	};

	// Sky textures
	const pendingSkyUpgrades: Array<{ key: string; targetTexture: string }> = [];
	for (const skyOption of SKY_TEXTURES) {
		if (skyOption.key === 'background' || !skyOption.texture) continue;
		const info = createSkyTextureInfo(skyOption.key, effectiveTextureQuality);
		if (info) {
			registerAndCollect(info);
			// Track if this sky needs an async upgrade
			const selection = selectSkyTexturesForQuality(skyOption.textures, skyOption.texture, effectiveTextureQuality);
			if (selection.needsUpgrade) {
				pendingSkyUpgrades.push({ key: skyOption.key, targetTexture: selection.targetTexture });
			}
		}
	}
	(self as any)._pendingSkyUpgrades = pendingSkyUpgrades;

	// Floor textures
	for (const key of Object.keys(FLOOR_TEXTURE_DEFS)) {
		const info = createFloorTextureInfo(key);
		if (info) registerAndCollect(info);
	}

	// Countertop textures
	for (const key of Object.keys(COUNTERTOP_TEXTURE_DEFS)) {
		const info = createCountertopTextureInfo(key);
		if (info) registerAndCollect(info);
	}

	// Surround textures
	for (const key of Object.keys(SURROUND_TEXTURE_DEFS)) {
		const info = createSurroundTextureInfo(key);
		if (info) registerAndCollect(info);
	}

	// Trim textures
	for (const key of Object.keys(TRIM_TEXTURE_DEFS)) {
		const info = createTrimTextureInfo(key);
		if (info) registerAndCollect(info);
	}

	// Appliance textures
	for (const key of getApplianceTextureKeys()) {
		const info = createApplianceTextureInfo(key);
		if (info) registerAndCollect(info);
	}

	// Load textures via TextureManager
	if (self.configAccess.textureManager) {
		await self.configAccess.textureManager.loadTextures(textures, {
			onComplete: () => {
				for (const tx of textures) {
					const material = self.configAccess.textureManager?.getMaterial(tx.key);
					if (material) {
						self.materialsAccess.set(tx.key, material as MeshStandardMaterial);
					}
				}
				self.geometryBuilderAccess.setMaterials(self.materialsAccess);

				// CRITICAL: Update ModelManager with materials for Collada model material replacement
				if (self.configAccess.modelManager) {
					self.configAccess.modelManager.updateConfig({
						materialsMap: self.materialsAccess
					});
				}
			}
		});
	} else {
		console.error('KitchenSceneBuilder.loadTextures: No textureManager provided!');
	}
};

// ============================================================================
// PHASED TEXTURE LOADING FUNCTIONS
// ============================================================================

/**
 * Material property constants
 */
const MATERIAL_CONSTANTS = {
	rough: 0.8,
	glassRoughness: 0.01,
	metalRoughness: 0.28,
	metalEnvMapIntensity: 1.045,
	stainlessWarmTint: new Color(1.03, 1.0, 0.97)
};

/**
 * Floor texture definitions by key
 */
const FLOOR_TEXTURE_DEFS: Record<string, { texture: string; normalMap: string; scale: number; displacementMap?: string; displacementScale?: number; roughness?: number; ktx2?: string }> = {
	'floor/grid-tile': { texture: 'grid-tile.png', normalMap: '', scale: 800 },
	'floor/textured-beige': { texture: 'textured-beige-floor.jpg', normalMap: 'textured-beige-floor_NRM.jpg', scale: 800 },
	'floor/hide': { texture: '', normalMap: '', scale: 1 },
	'floor/pebble-embedded-concrete': { texture: 'pebble_embedded_concrete_02_diff_1k.jpg', normalMap: 'pebble_embedded_concrete_02_nor_gl_1k.jpg', scale: 400 },
	'floor/asphalt-01': { texture: 'asphalt_01_diff_1k.jpg', normalMap: 'asphalt_01_nor_gl_1k.jpg', scale: 400 },
	'floor/brick-moss-001': { texture: 'brick_moss_001_diff_1k.jpg', normalMap: 'brick_moss_001_nor_gl_1k.jpg', scale: 400 },
	'floor/brick-pavement-02': { texture: 'brick_pavement_02_diff_1k.jpg', normalMap: 'brick_pavement_02_nor_gl_1k.jpg', scale: 400 },
	'floor/concrete-pavers': { texture: 'concrete_pavers_diff_1k.jpg', normalMap: 'concrete_pavers_nor_gl_1k.jpg', scale: 400, displacementMap: 'concrete_pavers_disp_1k.jpg', displacementScale: 1.0 },
	'floor/diagonal-parquet': { texture: 'diagonal_parquet_diff_1k.jpg', normalMap: 'diagonal_parquet_nor_gl_1k.jpg', scale: 400, roughness: 0.1 },
	'floor/granite-tile': { texture: 'granite_tile_diff_1k.jpg', normalMap: 'granite_tile_nor_gl_1k.jpg', scale: 400 },
	'floor/gravel-embedded-concrete': { texture: 'gravel_embedded_concrete_diff_1k.jpg', normalMap: 'gravel_embedded_concrete_nor_gl_1k.jpg', scale: 400 },
	'floor/herringbone-parquet': { texture: 'herringbone_parquet_diff_1k.jpg', normalMap: 'herringbone_parquet_nor_gl_1k.jpg', scale: 400, roughness: 0.1 },
	'floor/herringbone-pavement': { texture: 'herringbone_pavement_diff_1k.jpg', normalMap: 'herringbone_pavement_nor_gl_1k.jpg', scale: 200 },
	'floor/large-sandstone-blocks': { texture: 'large_sandstone_blocks_01_diff_1k.jpg', normalMap: 'large_sandstone_blocks_01_nor_gl_1k.jpg', scale: 400, displacementMap: 'large_sandstone_blocks_01_disp_1k.jpg', displacementScale: 2.2 },
	'floor/old-wood-floor': { texture: 'old_wood_floor_diff_1k.jpg', normalMap: 'old_wood_floor_nor_gl_1k.jpg', scale: 400 },
	'floor/pavement-02': { texture: 'pavement_02_diff_1k.jpg', normalMap: 'pavement_02_nor_gl_1k.jpg', scale: 400 },
	'floor/pavement-03': { texture: 'pavement_03_diff_1k.jpg', normalMap: 'pavement_03_nor_gl_1k.jpg', scale: 400 },
	'floor/pavement-05': { texture: 'pavement_05_diff_1k.jpg', normalMap: 'pavement_05_nor_gl_1k.jpg', scale: 400 },
	'floor/patterned-cobblestone': { texture: 'patterned_cobblestone_diff_1k.jpg', normalMap: 'patterned_cobblestone_nor_gl_1k.jpg', scale: 200, displacementMap: 'patterned_cobblestone_disp_1k.jpg', displacementScale: 1.0 },
	'floor/sparse-grass': { texture: 'sparse_grass_diff_1k.jpg', normalMap: 'sparse_grass_nor_gl_1k.jpg', scale: 400, displacementMap: 'sparse_grass_disp_1k.jpg', displacementScale: 2.0, roughness: 1.0 },
	'floor/square-tiles': { texture: 'square_tiles_02_diff_1k.jpg', normalMap: 'square_tiles_02_nor_gl_1k.jpg', scale: 400 },
	'floor/stone-tiles': { texture: 'stone_tiles_02_diff_1k.jpg', normalMap: 'stone_tiles_02_nor_gl_1k.jpg', scale: 400, displacementMap: 'stone_tiles_02_disp_1k.jpg', displacementScale: 1.0 },
	'floor/t-brick-floor': { texture: 't_brick_floor_002_diffuse_1k.jpg', normalMap: 't_brick_floor_002_nor_gl_1k.jpg', scale: 400 },
	'floor/weathered-brown-planks': { texture: 'weathered_brown_planks_diff_1k.jpg', normalMap: 'weathered_brown_planks_nor_gl_1k.jpg', scale: 400 },
	'floor/wood-floor': { texture: 'wood_floor_diff_1k.jpg', normalMap: 'wood_floor_nor_gl_1k.jpg', scale: 400, roughness: 0.2 },
	'floor/wood-floor-deck': { texture: 'wood_floor_deck_diff_1k.jpg', normalMap: 'wood_floor_deck_nor_gl_1k.jpg', scale: 400 },
	'floor/wood-planks-grey': { texture: 'wood_planks_grey_diff_1k.jpg', normalMap: 'wood_planks_grey_nor_gl_1k.jpg', scale: 400 }
};

/**
 * Countertop texture definitions by key
 */
// All countertop textures use scale: 0.5 for proper tiling on the cap face (world-space UVs).
// chiseledBump controls the ChiseledEdgeBump normal map on extruded side faces:
//   - chiseled edges: chiseledBump defaults to true (bumpy edges)
//   - clean edge / granite / moks: chiseledBump: false (smooth polished edges)
const COUNTERTOP_TEXTURE_DEFS: Record<string, { texture: string; hdr: boolean; scale?: number; envMapIntensity?: number; roughness?: number; chiseledBump?: boolean; color?: [number, number, number]; ktx2?: string }> = {
	'chiseledhickory': { texture: 'ChiseledEdge_Brown_Tile.jpg', hdr: false, scale: 0.5, envMapIntensity: 0.0, roughness: 0.0 },
	'chiseledcloudy': { texture: 'ChiseledEdge_White_Tile.jpg', hdr: false, scale: 0.5, envMapIntensity: 0.1 },
	'chiseledsmoky': { texture: 'ChiseledEdge_Gray_Tile.jpg', hdr: false, scale: 0.5 },
	'cleanedgehickory': { texture: 'ChiseledEdge_Brown_Tile.jpg', hdr: false, scale: 0.5, chiseledBump: false },
	'cleanedgecloudy': { texture: 'ChiseledEdge_White_Tile.jpg', hdr: false, scale: 0.5, envMapIntensity: 0.1, chiseledBump: false },
	'cleanedgesmoky': { texture: 'ChiseledEdge_Gray_Tile.jpg', hdr: false, scale: 0.5, chiseledBump: false },
	'cleanedgeslate': { texture: 'CleanEdge_Slate_Tile.jpg', hdr: false, scale: 0.5, chiseledBump: false },
	'granite|fantasybrown': { texture: 'Fantasy-Brown.jpg', hdr: false, scale: 0.5, envMapIntensity: 4, chiseledBump: false },
	'granite|steelgray': { texture: 'Steel-Gray.jpg', hdr: false, scale: 0.5, envMapIntensity: 0.2, chiseledBump: false },
	// Reported as "looks white" (#382): at this texture's native luminance, ACES tone mapping
	// (exposure 1.2) alone collapses R/G from 1.09 to ~1.04; color/envMapIntensity below are sized from that pipeline's arithmetic (see #382 investigation), not eyeballed.
	'granite|venetiangold': { texture: 'Venetian-Gold.jpg', hdr: false, scale: 0.5, envMapIntensity: 0.0, chiseledBump: false, color: [0.66, 0.51, 0.36] },
	'granite|viscountwhite': { texture: 'Cloud-White.jpg', hdr: false, scale: 0.5, envMapIntensity: 0.0, chiseledBump: false },
	'granite|alfawhite': { texture: 'Alfa-White.jpg', hdr: false, scale: 0.5, envMapIntensity: 0.0, chiseledBump: false },
	'granite|thunderwhite': { texture: 'Thunder-White.jpg', hdr: false, scale: 0.5, envMapIntensity: 0.0, chiseledBump: false },
	'granite|giallowhite': { texture: 'Giallo-White.jpg', hdr: false, scale: 0.5, envMapIntensity: 0.0, chiseledBump: false },
	'moks|doveshimmer': { texture: 'Dove_shimmer.jpg', hdr: false, scale: 0.5, chiseledBump: false },
	'moks|galacticice': { texture: 'Galactic-Ice-Countertop.jpeg', hdr: false, scale: 0.5, chiseledBump: false },
	'moks|asteroidterrazzo': { texture: 'Asteroid-glimmer.jpg', hdr: false, scale: 2.0, chiseledBump: false },
	// "No Countertop" placeholder: gray clean-edge texture with a red multiply tint so it reads
	// as a non-RTA countertop. Key stays 'none' so pricing/clean-edge logic is untouched.
	'none': { texture: 'CleanEdge_Slate_Tile.jpg', hdr: false, scale: 0.5, chiseledBump: false, color: [1.0, 0.7, 0.7] }
};

/**
 * Surround texture definitions by key
 */
const SURROUND_TEXTURE_DEFS: Record<string, { texture: string; normalMap: string; displacementMap: string; displacementScale: number; scale: number; envMapIntensity: number; metal?: number; rough?: number; baseColor?: number; surfaceTint?: number; ktx2?: string }> = {
	'stackedstone/graphite': { texture: 'StackedStone_Gray_Tile.jpg', normalMap: 'StackedStone_Gray_Tile_NRM.jpg', displacementMap: '', displacementScale: 0.0, scale: 0.7, envMapIntensity: 0.18, metal: 0.0, rough: 1.0, baseColor: 0x808080 },
	'stackedstone/chalk': { texture: 'StackedStone_White_Tile.jpg', normalMap: 'StackedStone_White_Tile_NRM.jpg', displacementMap: '', displacementScale: 0.0, scale: 0.7, envMapIntensity: 0.25, metal: 0.12, rough: 1.0, baseColor: 0xe0ddd8 },
	'stackedstone/terra': { texture: 'StackedStone_Brown_Tile.jpg', normalMap: 'StackedStone_Brown_Tile_NRM.jpg', displacementMap: '', displacementScale: 0.0, scale: 0.7, envMapIntensity: 0.0, metal: 0.2, rough: 1.0, baseColor: 0xa0785a },
	'reclaimedbrick/white': { texture: 'Brick_WhiteRustic_Tile.jpg', normalMap: 'Brick_WhiteRustic_Tile_NRM.jpg', displacementMap: '', displacementScale: 0.0, scale: 0.7, envMapIntensity: 0.5, metal: 0.0, rough: 1.0, baseColor: 0xd0c8c0 },
	'weatheredwood/umber': { texture: 'Weaathered_Wood_Brown_Tile.jpg', normalMap: 'Weaathered_Wood_Brown_Tile_NRM.jpg', displacementMap: '', displacementScale: 0.0, scale: 0.4, envMapIntensity: 0.15, metal: 0.25, rough: 1.0, baseColor: 0x8b6e5a },
	'weatheredwood/ash': { texture: 'Weathered_Wood_Gray_Tile.jpg', normalMap: 'Weathered_Wood_Gray_Tile_NRM.jpg', displacementMap: '', displacementScale: 0.0, scale: 0.4, envMapIntensity: 0.15, metal: 0.25, rough: 1.0, baseColor: 0xa8a8a8 },
	'modernconcrete/bright': { texture: 'Modern_White_Tile.jpg', normalMap: '', displacementMap: '', displacementScale: 0.0, scale: 1.0, envMapIntensity: 0.12, metal: 0.0, rough: 1.0, baseColor: 0xe0e0e0 },
	'modernconcrete/industrial': { texture: 'Modern_LightGray_Tile.jpg', normalMap: '', displacementMap: '', displacementScale: 0.0, scale: 1.0, envMapIntensity: 0.0, metal: 0.44, rough: 1.0, baseColor: 0xc8c8c8 },
	'plank/bright': { texture: 'Shiplap_White_Tile.jpg', normalMap: 'Shiplaps_NormalMap.jpg', displacementMap: '', displacementScale: 0.0, scale: 0.7, envMapIntensity: 0.31, metal: 0.0, rough: 1.0, baseColor: 0xc8c0b8 },
	'plank/charcoal': { texture: 'Shiplap_Gray_Tile.jpg', normalMap: 'Shiplaps_NormalMap.jpg', displacementMap: '', displacementScale: 0.0, scale: 0.7, envMapIntensity: 0.15, metal: 0.0, rough: 1.0, baseColor: 0x6a6a6a },
	// metal 0 + small envMapIntensity keeps the diffuse texture reading white — a nonzero metalness with a
	// zero env-map contribution desaturated the surface (reported as "looks gray", #214 / #309).
	'modernstone/bright': { texture: 'Modern_Stone_Brigh_Tile.jpg', normalMap: 'Modern_Stone_Brigh_Tile_NRM.jpg', displacementMap: '', displacementScale: 0.0, scale: 0.4, envMapIntensity: 0.12, metal: 0.0, rough: 1.0 },
	'modernstone/ash': { texture: 'Modern_Stone_Charcoal_Tile.jpg', normalMap: 'Modern_Stone_Charcoal_Tile_NRM.jpg', displacementMap: '', displacementScale: 0.0, scale: 0.4, envMapIntensity: 0.18, metal: 0.2, rough: 1.0 },
	// Slat finishes: flat matte color, no diffuse map. Slat ridges come from Slat.glb geometry; lighting alone defines the profile.
	// metal/rough/envMapIntensity values baked from workbench calibration overrides (saved via the calibration UI).
	'slatbright': { texture: '', normalMap: '', displacementMap: '', displacementScale: 0.0, scale: 1.0, envMapIntensity: 0.35, metal: 0.35, rough: 0.59, surfaceTint: 0xd5d2cb, baseColor: 0xdcdcdc },
	'slatwalnut': { texture: '', normalMap: '', displacementMap: '', displacementScale: 0.0, scale: 1.0, envMapIntensity: 0.39, metal: 0.48, rough: 0.31, surfaceTint: 0xa98972 },
	'slatcharcoal': { texture: '', normalMap: '', displacementMap: '', displacementScale: 0.0, scale: 1.0, envMapIntensity: 0.8, metal: 0.21, rough: 0.54, surfaceTint: 0x2a2a2a, baseColor: 0x505050 },
	'moks/purewhite': { texture: 'msurround1.png', normalMap: 'MOKS_nrm.jpg', displacementMap: '', displacementScale: 20.0, scale: 1.0, envMapIntensity: 1.0, metal: 0.0, rough: 1.0, baseColor: 0xc8c8c8 },
	'moks/pitchblack': { texture: 'msurround4.png', normalMap: 'MOKS_nrm.jpg', displacementMap: '', displacementScale: 20.0, scale: 1.0, envMapIntensity: 1.0, metal: 0.0, rough: 1.0, baseColor: 0x1a1a1a },
	'moks/slategray': { texture: 'msurround8.png', normalMap: 'MOKS_nrm.jpg', displacementMap: '', displacementScale: 20.0, scale: 1.0, envMapIntensity: 0.0, metal: 0.25, rough: 1.0, baseColor: 0x888a8c },
	'scratchcoat': { texture: 'Modern_LightGray_Tile.jpg', normalMap: '', displacementMap: '', displacementScale: 0.0, scale: 1.0, envMapIntensity: 0.0, metal: 0.15, rough: 1.0, baseColor: 0xb0b0b0 }
};

/**
 * Trim texture definitions by key
 */
const TRIM_TEXTURE_DEFS: Record<string, { texture: string; envMapIntensity: number; transparent?: boolean; scale?: number; metalness?: number; roughness?: number; baseColor?: number; ktx2?: string }> = {
	'stackedstone/chalk-corner': { texture: 'CornerTexture_White.jpg', envMapIntensity: 0.23 },
	'reclaimedbrick/white-corner': { texture: 'CornerTexture_White.jpg', envMapIntensity: 0.5 },
	'modernconcrete/bright-corner': { texture: 'CornerTexture_White.jpg', envMapIntensity: 0.94 },
	'plank/bright-corner': { texture: 'CornerTexture_White.jpg', envMapIntensity: 0.9 },
	'modernstone/bright-corner': { texture: 'CornerTexture_White.jpg', envMapIntensity: 0.0 },
	'stackedstone/graphite-corner': { texture: 'CornerTexture_Gray.jpg', envMapIntensity: 0.25 },
	'weatheredwood/ash-corner': { texture: 'CornerTexture_Gray.jpg', envMapIntensity: 0.0, metalness: 0.34, roughness: 0.0 },
	'modernconcrete/industrial-corner': { texture: 'Modern_LightGray_Tile.jpg', envMapIntensity: 0.0, scale: 1.0, metalness: 0.44, roughness: 1.0, baseColor: 0xe4e4e4 },
	'stackedstone/terra-corner': { texture: 'CornerTexture_Brown.jpg', envMapIntensity: 1.26 },
	'weatheredwood/umber-corner': { texture: 'CornerTexture_Brown.jpg', envMapIntensity: 0.77 },
	'plank/charcoal-corner': { texture: 'CornerTexture_Black.jpg', envMapIntensity: 0.0 },
	'modernstone/ash-corner': { texture: 'CornerTexture_Black.jpg', envMapIntensity: 0.81 },
	// Corner trim values baked from workbench calibration overrides.
	'slatbright-corner': { texture: 'Steel_Br.jpg', envMapIntensity: 0.76, scale: 1.0, metalness: 0.37, roughness: 0.05 },
	'slatwalnut-corner': { texture: 'Steel_Br.jpg', envMapIntensity: 1.39, scale: 1.0, metalness: 0.15, roughness: 0.12, baseColor: 0x9e8167 },
	'slatcharcoal-corner': { texture: 'Steel_Br.jpg', envMapIntensity: 0.6, scale: 1.0, metalness: 0.44, roughness: 0.16, baseColor: 0x3a3a3a },
	'moks/pitchblack-corner': { texture: 'transparent.png', envMapIntensity: 0, transparent: true },
	'moks/purewhite-corner': { texture: 'transparent.png', envMapIntensity: 0, transparent: true },
	'moks/slategray-corner': { texture: 'transparent.png', envMapIntensity: 0, transparent: true },
	'scratchcoat-corner': { texture: 'CornerTexture_Gray.jpg', envMapIntensity: 0.5 },
	'general/transparent': { texture: 'transparent.png', envMapIntensity: 0, transparent: true }
};

/**
 * Appliance texture definitions by key.
 * Brushed stainless steel tuned for ACES + PMREM pipeline:
 * - Moderate metalness preserves shadow contrast
 * - Roughness 0.28 gives brushed look without mirror-like wash-out under ACES
 */
const APPLIANCE_TEXTURE_DEFS: Record<string, { texture: string | null; normalMap: string; scale: number | null; metalness: number; roughness: number; envMapIntensity: number; color: [number, number, number] | null; ktx2?: string }> = {
	'Steel_Br-material': { texture: 'Steel_Br.jpg', normalMap: '', scale: 1.0, metalness: 0.94, roughness: 0.18, envMapIntensity: 1.6, color: [1.03, 1.0, 0.97] },
	'__brushe-material': { texture: 'Steel_Br.jpg', normalMap: '', scale: 1.0, metalness: 0.92, roughness: 0.16, envMapIntensity: 1.6, color: [1.03, 1.0, 0.97] },
	'Metal_Al-material': { texture: 'Metal_Al.jpg', normalMap: '', scale: 1.0, metalness: 0.9, roughness: 0.22, envMapIntensity: 1.6, color: [1.03, 1.0, 0.97] },
	'Metal_Ro-material': { texture: 'Metal_Ro.jpg', normalMap: '', scale: 1.0, metalness: 0.9, roughness: 0.22, envMapIntensity: 1.6, color: [1.03, 1.0, 0.97] },
	'Metal_Ru-material': { texture: 'Metal_Ru.jpg', normalMap: '', scale: 1.0, metalness: 0.9, roughness: 0.22, envMapIntensity: 1.6, color: [1, 0, 0] },
	'termomet-material': { texture: 'termomet.jpg', normalMap: '', scale: null, metalness: 0, roughness: 0.9, envMapIntensity: 0, color: null },
	'LCD-material': { texture: 'LCD.jpg', normalMap: '', scale: null, metalness: 0, roughness: 0.9, envMapIntensity: 0, color: null },
	'Copper-material': { texture: null, normalMap: 'copper_NRM.jpg', scale: 360.0, metalness: 0.2, roughness: 0.01, envMapIntensity: 1.6, color: [0.02445872574396036, 0.003077806501351008, 0.001200146284752912] },
	'Outer_Sh-material': { texture: null, normalMap: 'Outer_Sh_NRM.jpg', scale: 80.0, metalness: 0.9, roughness: 0.1, envMapIntensity: 1.6, color: [0.010960101697233708, 0.01938235916419186, 0.01298303977002583] },
	'Thermometer2-material': { texture: 'thermometer.jpg', normalMap: '', scale: null, metalness: 0, roughness: 0, envMapIntensity: 0, color: null },
	'FlameSymbol-material': { texture: 'Flame.png', normalMap: '', scale: null, metalness: 0, roughness: 0, envMapIntensity: 0, color: null },
	'LeGriddleLogo-material': { texture: 'LeGriddleLogo2023.png', normalMap: '', scale: null, metalness: 0, roughness: 0, envMapIntensity: 0, color: null },
	'WoodTexture-material': { texture: 'WOOD-texture.jpg', normalMap: 'WOOD-texture-NRM.jpg', scale: null, metalness: 0.2, roughness: 0.8, envMapIntensity: 0.5, color: null }
};

/**
 * Create TextureInfo for a floor texture
 */
export const createFloorTextureInfo = (key: string): TextureInfo | null => {
	const def = FLOOR_TEXTURE_DEFS[key];
	if (!def) return null;

	return {
		key,
		texture: def.texture,
		handleAsHdr: false,
		alphaMapPath: '',
		normalMapPath: def.normalMap,
		normalScale: new Vector2(1, 1),
		displacementMapPath: def.displacementMap || '',
		displacementScale: def.displacementScale || 0.0,
		displacementBias: 0.0,
		scale: def.scale,
		metalness: 0.3,
		roughness: def.roughness ?? 0.66,
		transparent: false,
		brightness: null,
		envMapIntensity: 1.0,
		color: null,
		textureType: TextureType.Floor,
		loadPriority: TextureLoadPriority.CRITICAL,
		ktx2Path: def.ktx2
	};
};

/**
 * Create TextureInfo for a sky texture
 */
export const createSkyTextureInfo = (key: string, renderQuality: number): TextureInfo | null => {
	const skyOption = SKY_TEXTURES.find((s) => s.key === key);
	if (!skyOption || skyOption.key === 'background' || !skyOption.texture) {
		return null;
	}

	const selection = selectSkyTexturesForQuality(
		skyOption.textures,
		skyOption.texture,
		renderQuality
	);

	if (!selection.defaultTexture) return null;

	const isHdr = selection.defaultTexture.endsWith('.hdr');
	const ktx2Path = selectKtx2SkyTexture(skyOption.ktx2Textures, skyOption.ktx2Texture, renderQuality);

	return {
		key,
		texture: selection.defaultTexture,
		handleAsHdr: isHdr,
		alphaMapPath: '',
		normalMapPath: '',
		normalScale: new Vector2(1, 1),
		displacementMapPath: '',
		displacementScale: 0.0,
		displacementBias: 0.0,
		scale: null,
		metalness: 0.2,
		roughness: MATERIAL_CONSTANTS.rough,
		transparent: false,
		brightness: null,
		envMapIntensity: 0.5,
		color: null,
		textureType: TextureType.Sky,
		loadPriority: TextureLoadPriority.CRITICAL,
		ktx2Path
	};
};

/**
 * Create TextureInfo for a countertop texture
 */
export const createCountertopTextureInfo = (key: string): TextureInfo | null => {
	const def = COUNTERTOP_TEXTURE_DEFS[key];
	if (!def) return null;

	// Only apply ChiseledEdgeBump normal map when chiseledBump is not explicitly false.
	// Granite countertops set chiseledBump: false to get a smooth polished edge.
	const normalMapPath = def.chiseledBump !== false ? 'ChiseledEdgeBump_NRM.jpg' : '';

	return {
		key,
		texture: def.texture,
		handleAsHdr: def.hdr,
		alphaMapPath: '',
		normalMapPath,
		normalScale: new Vector2(1, 1),
		displacementMapPath: '',
		displacementScale: 0.0,
		displacementBias: 0.0,
		scale: def.scale ?? null,
		metalness: 0.0,
		roughness: def.roughness ?? MATERIAL_CONSTANTS.rough,
		transparent: false,
		brightness: null,
		envMapIntensity: def.envMapIntensity ?? 0.2,
		color: def.color ? new Color(def.color[0], def.color[1], def.color[2]) : null,
		textureType: TextureType.Counter,
		loadPriority: TextureLoadPriority.SCENE,
		ktx2Path: def.ktx2
	};
};

/**
 * Create TextureInfo for a surround texture
 */
export const createSurroundTextureInfo = (key: string): TextureInfo | null => {
	const def = SURROUND_TEXTURE_DEFS[key];
	if (!def) return null;

	// baseColor is stashed in material.userData for the plain-material fallback
	// (edges/corners). For the textured surround surface, opt-in per-finish via
	// `surfaceTint` — applied as a multiplicative tint on top of the texture map.
	return {
		key,
		texture: def.texture,
		handleAsHdr: false,
		alphaMapPath: '',
		normalMapPath: def.normalMap,
		normalScale: new Vector2(1, 1),
		displacementMapPath: def.displacementMap,
		displacementScale: def.displacementScale,
		displacementBias: 0.0,
		scale: def.scale,
		metalness: def.metal ?? 0.2,
		roughness: def.rough ?? MATERIAL_CONSTANTS.rough,
		transparent: false,
		brightness: null,
		envMapIntensity: def.envMapIntensity,
		color: def.surfaceTint != null ? new Color(def.surfaceTint) : null,
		textureType: TextureType.Surround,
		loadPriority: TextureLoadPriority.SCENE,
		baseColor: def.baseColor,
		ktx2Path: def.ktx2
	};
};

/**
 * Create TextureInfo for a trim texture
 */
export const createTrimTextureInfo = (key: string): TextureInfo | null => {
	const def = TRIM_TEXTURE_DEFS[key];
	if (!def) return null;

	return {
		key,
		texture: def.texture,
		handleAsHdr: false,
		alphaMapPath: '',
		normalMapPath: '',
		normalScale: new Vector2(1, 1),
		displacementMapPath: '',
		displacementScale: 0.0,
		displacementBias: 0.0,
		scale: def.scale ?? 0.5,
		metalness: def.metalness ?? 0.2,
		roughness: def.roughness ?? MATERIAL_CONSTANTS.rough,
		transparent: def.transparent ?? false,
		brightness: null,
		envMapIntensity: def.envMapIntensity,
		color: def.baseColor !== undefined ? new Color(def.baseColor) : null,
		textureType: TextureType.Trim,
		loadPriority: TextureLoadPriority.SCENE,
		ktx2Path: def.ktx2
	};
};

/**
 * Create TextureInfo for an appliance texture
 */
export const createApplianceTextureInfo = (key: string): TextureInfo | null => {
	const def = APPLIANCE_TEXTURE_DEFS[key];
	if (!def) return null;

	return {
		key,
		texture: def.texture || '',
		handleAsHdr: false,
		alphaMapPath: '',
		normalMapPath: def.normalMap,
		normalScale: new Vector2(1, 1),
		displacementMapPath: '',
		displacementScale: 0.0,
		displacementBias: 0.0,
		scale: def.scale,
		metalness: def.metalness,
		roughness: def.roughness,
		transparent: false,
		brightness: null,
		envMapIntensity: def.envMapIntensity,
		color: def.color ? new Color(def.color[0], def.color[1], def.color[2]) : null,
		textureType: TextureType.Surround,
		ktx2Path: def.ktx2
	};
};

/**
 * Get all appliance texture keys
 */
export const getApplianceTextureKeys = (): string[] => Object.keys(APPLIANCE_TEXTURE_DEFS);

/**
 * Parameters for loading critical textures (floor + sky only)
 */
interface LoadCriticalTexturesParams {
	floorKey: string;
	skyKey: string;
}

/**
 * Phase 1: Load critical textures (selected floor + sky).
 * This blocks scene render until complete.
 */
export async function loadCriticalTexturesCfn (
	self: KitchenSceneBuilder,
	params: LoadCriticalTexturesParams
): Promise<void> {
	const { floorKey, skyKey } = params;
	const { renderQuality, textureQuality } = self.configAccess;
	const effectiveTextureQuality = getEffectiveTextureQuality(textureQuality, renderQuality);
	const textures: TextureInfo[] = [];

	// Create floor texture info
	const floorInfo = createFloorTextureInfo(floorKey);
	if (floorInfo) {
		self.textureInfoManagerAccess.addTexture(floorKey, floorInfo);
		textures.push(floorInfo);
	}

	// Create sky texture info (default/1K resolution - quality upgrade deferred until after models load)
	const skyInfo = createSkyTextureInfo(skyKey, effectiveTextureQuality);
	if (skyInfo) {
		self.textureInfoManagerAccess.addTexture(skyKey, skyInfo);
		textures.push(skyInfo);
	}

	// Also load the reflection sky so it is available for material reflections.
	// This is decoupled from the visible background — metallic surfaces always
	// reflect REFLECTION_SKY_KEY regardless of which sky the user selects.
	const REFLECTION_SKY_KEY = 'sky/rainforest_trail';
	if (skyKey !== REFLECTION_SKY_KEY) {
		const reflectionSkyInfo = createSkyTextureInfo(REFLECTION_SKY_KEY, effectiveTextureQuality);
		if (reflectionSkyInfo) {
			self.textureInfoManagerAccess.addTexture(REFLECTION_SKY_KEY, reflectionSkyInfo);
			textures.push(reflectionSkyInfo);
		}
	}

	// Design-package match: force-load the overcast HDR so it is available as scene.environment.
	if (DESIGN_PACKAGE_LOOK && skyKey !== DESIGN_PACKAGE_ENV_SKY_KEY) {
		const designEnvInfo = createSkyTextureInfo(DESIGN_PACKAGE_ENV_SKY_KEY, effectiveTextureQuality);
		if (designEnvInfo) {
			self.textureInfoManagerAccess.addTexture(DESIGN_PACKAGE_ENV_SKY_KEY, designEnvInfo);
			textures.push(designEnvInfo);
		}
	}

	// Load textures in a single batch
	if (self.configAccess.textureManager && textures.length > 0) {
		await self.configAccess.textureManager.loadTextures(textures, {
			onComplete: () => {
				for (const tx of textures) {
					const material = self.configAccess.textureManager?.getMaterial(tx.key);
					if (material) {
						self.materialsAccess.set(tx.key, material as MeshStandardMaterial);
					}
				}
				self.geometryBuilderAccess.setMaterials(self.materialsAccess);

				// Mark loaded textures as complete in the queue so waitForTexture resolves
				const queue = self.textureLoadingQueueAccess;
				for (const tx of textures) {
					queue.markAsLoaded(tx.key);
				}

				// Call onSkyReady callback to set sky background immediately
				if (skyKey && self.configAccess.onSkyReady && !(self as any)._skyReadyCalled) {
					(self as any)._skyReadyCalled = true;
					self.configAccess.onSkyReady(skyKey);
				}
			}
		});
	}
}

/**
 * Scene materials loading parameters
 */
interface LoadSceneMaterialsParams {
	sceneMaterials: {
		surround: string;
		countertop: string;
		countertops?: string[];
		trim: string;
	};
}

/**
 * Phase 2: Load scene materials (surround, countertop, trim).
 * These are loaded after critical textures so geometry creation can proceed.
 */
export async function loadSceneMaterialsCfn (
	self: KitchenSceneBuilder,
	params: LoadSceneMaterialsParams
): Promise<void> {
	const { sceneMaterials } = params;
	const textures: TextureInfo[] = [];

	const surroundInfo = createSurroundTextureInfo(sceneMaterials.surround);
	if (surroundInfo) {
		self.textureInfoManagerAccess.addTexture(surroundInfo.key, surroundInfo);
		textures.push(surroundInfo);
	}

	const countertopKeys = sceneMaterials.countertops ?? [sceneMaterials.countertop];
	for (const countertopKey of countertopKeys) {
		const countertopInfo = createCountertopTextureInfo(countertopKey);
		if (countertopInfo) {
			self.textureInfoManagerAccess.addTexture(countertopInfo.key, countertopInfo);
			textures.push(countertopInfo);
		}
	}

	const trimInfo = createTrimTextureInfo(sceneMaterials.trim);
	if (trimInfo) {
		self.textureInfoManagerAccess.addTexture(trimInfo.key, trimInfo);
		textures.push(trimInfo);
	}

	if (self.configAccess.textureManager && textures.length > 0) {
		await self.configAccess.textureManager.loadTextures(textures, {
			onComplete: () => {
				for (const tx of textures) {
					const material = self.configAccess.textureManager?.getMaterial(tx.key);
					if (material) {
						self.materialsAccess.set(tx.key, material as MeshStandardMaterial);
					}
				}
				self.geometryBuilderAccess.setMaterials(self.materialsAccess);

				// Mark loaded textures as complete in the queue so waitForTexture resolves
				const queue = self.textureLoadingQueueAccess;
				for (const tx of textures) {
					queue.markAsLoaded(tx.key);
				}
			}
		});
	}
}

/**
 * Delay in milliseconds before deferred preloads begin.
 * Gives the scene time to finish rendering and become interactive.
 */
const DEFERRED_PRELOAD_DELAY_MS = 3000;

/**
 * Schedule a callback to run when the browser is idle, falling back to
 * setTimeout for environments that do not support requestIdleCallback.
 */
const scheduleWhenIdle = (callback: () => void, delayMs: number): void => {
	if (typeof requestIdleCallback === 'function') {
		requestIdleCallback(() => { callback(); }, { timeout: delayMs * 2 });
	} else {
		setTimeout(callback, delayMs);
	}
};

/**
 * Enqueue all background preload textures into the loading queue.
 * Shared by both 'all' and 'deferred' strategies — the only difference
 * is *when* this function is called.
 */
const enqueuePreloads = (
	self: KitchenSceneBuilder,
	requirements: SceneTextureRequirements,
	effectiveTextureQuality: number
): void => {
	const queue = self.textureLoadingQueueAccess;

	// Preload other countertop/surround textures
	for (const key of requirements.preloadMaterials) {
		// Check if it's a countertop or surround key
		if (VALID_COUNTERTOP_KEYS.has(key)) {
			const info = createCountertopTextureInfo(key);
			if (info) {
				self.textureInfoManagerAccess.addTexture(key, info);
				queue.enqueue(info, TextureLoadPriority.PRELOAD);
			}
		} else if (VALID_SURROUND_KEYS.has(key)) {
			const info = createSurroundTextureInfo(key);
			if (info) {
				self.textureInfoManagerAccess.addTexture(key, info);
				queue.enqueue(info, TextureLoadPriority.PRELOAD);
			}
			// Also preload the matching corner trim texture
			const cornerKey = `${key}-corner`;
			const trimInfo = createTrimTextureInfo(cornerKey);
			if (trimInfo) {
				self.textureInfoManagerAccess.addTexture(cornerKey, trimInfo);
				queue.enqueue(trimInfo, TextureLoadPriority.PRELOAD);
			}
		}
	}

	// Preload other floor textures (skip sky textures — their PMREM
	// generation blocks the main thread and freezes interaction)
	for (const key of requirements.preloadEnvironment) {
		if (key.startsWith('floor/')) {
			const info = createFloorTextureInfo(key);
			if (info) {
				info.loadPriority = TextureLoadPriority.PRELOAD;
				self.textureInfoManagerAccess.addTexture(key, info);
				queue.enqueue(info, TextureLoadPriority.PRELOAD);
			}
		}
		// Sky textures are NOT preloaded — each one triggers a synchronous
		// PMREM fromEquirectangular() call (~200ms GPU block) in createMaterials.
		// They load on-demand when the user switches sky.
	}
};

/**
 * Phase 5-7: Queue background preload textures.
 * This is non-blocking — textures preload for instant switching later.
 *
 * Behaviour is controlled by preloadStrategy (default: 'deferred'):
 * - 'all'      — enqueue immediately after build completes
 * - 'deferred' — wait until the browser is idle (requestIdleCallback / 3s fallback)
 *                then enqueue, so the initial scene render is never blocked
 * - 'none'     — skip background preloads entirely
 */
export const queueBackgroundPreloadsCfn = (
	self: KitchenSceneBuilder,
	{ input, requirements }: { input: KitchenBlueprintInput; requirements: SceneTextureRequirements }
): void => {
	const { preloadStrategy = 'deferred', renderQuality, textureQuality } = self.configAccess;

	// Strategy 'none' skips all background preloading
	if (preloadStrategy === 'none') {
		return;
	}

	// Controller may opt out of preloading entirely — on-demand loading only.
	// Used to avoid OOM crashes on low-memory mobile browsers.
	if (input.controller?.preloadAssets === false) {
		return;
	}

	// On low-memory devices (iOS Safari in particular) the PRELOAD tier is the
	// primary source of idle-load OOM: it queues every alternate floor/sky/
	// surround/countertop even when only the currently selected ones are used.
	// navigator.deviceMemory reports ≤4 on iPhones and low-end Androids. Modern
	// desktop browsers cap the value at 8. Fall back to 8 on undefined so
	// unknown-device Safari behaves like desktop.
	const nav = typeof navigator !== 'undefined' ? navigator : undefined;
	const deviceMemory = (nav as { deviceMemory?: number } | undefined)?.deviceMemory ?? 8;
	if (deviceMemory < 4) {
		return;
	}

	const effectiveTextureQuality = getEffectiveTextureQuality(textureQuality, renderQuality);

	if (preloadStrategy === 'deferred') {
		// Wait until the browser is idle before enqueuing background preloads.
		// This ensures the initial scene render and camera interaction are smooth.
		scheduleWhenIdle(
			() => { enqueuePreloads(self, requirements, effectiveTextureQuality); },
			DEFERRED_PRELOAD_DELAY_MS
		);
	} else {
		// 'all' — enqueue immediately
		enqueuePreloads(self, requirements, effectiveTextureQuality);
	}
};

/**
 * Parameters for loading appliance textures
 */
export type LoadApplianceTexturesParams = {
	/** Model requirements from analyzeTextureRequirementsCfn — used to skip loading entirely when no appliances are placed */
	modelRequirements: Array<ModelRequirement>;
};

/**
 * Load appliance textures needed for model material replacement.
 * Only loads textures when appliances are actually placed in the kitchen.
 * When appliances are present, restricts the loaded set to the unique texture keys
 * reachable via MATERIAL_ALIAS_MAP — the only keys adjustModelMaterials can look up.
 * Uses the shared APPLIANCE_TEXTURE_DEFS as single source of truth.
 */
export async function loadApplianceTexturesCfn (
	self: KitchenSceneBuilder,
	params: LoadApplianceTexturesParams
): Promise<void> {
	const { modelRequirements } = params;

	// No appliances placed — skip all appliance texture downloads entirely
	if (modelRequirements.length === 0) {
		return;
	}

	// Only load texture keys that adjustModelMaterials can actually reference.
	// getReachableApplianceTextureKeys() returns the unique values of MATERIAL_ALIAS_MAP,
	// which is the complete set of keys that model material replacement can look up.
	const reachableKeys = getReachableApplianceTextureKeys();
	const textures: TextureInfo[] = [];

	for (const key of getApplianceTextureKeys()) {
		if (!reachableKeys.has(key)) continue;
		const info = createApplianceTextureInfo(key);
		if (info) {
			self.textureInfoManagerAccess.addTexture(key, info);
			textures.push(info);
		}
	}

	if (self.configAccess.textureManager && textures.length > 0) {
		await self.configAccess.textureManager.loadTextures(textures, {
			onComplete: () => {
				for (const tx of textures) {
					const material = self.configAccess.textureManager?.getMaterial(tx.key);
					if (material) {
						self.materialsAccess.set(tx.key, material as MeshStandardMaterial);
					}
				}
				// Update ModelManager with materials for Collada model material replacement
				if (self.configAccess.modelManager) {
					self.configAccess.modelManager.updateConfig({
						materialsMap: self.materialsAccess
					});
				}
			}
		});
	}
}

// ============================================================================
// LEGACY FUNCTION (kept for backwards compatibility)
// ============================================================================

/**
 * Get floor material
 */
export const getFloorMaterialCfn = (
	self: KitchenSceneBuilder,
	{ floorKey }: { floorKey?: string | null }
): MeshStandardMaterial => {
	const key = floorKey || 'floor/grid-tile';
	const material = self.materialsAccess.get(key);
	if (material) {
		return material;
	}
	// Fallback: create a simple grid material if texture not loaded
	console.warn('KitchenSceneBuilder.getFloorMaterial: Floor material not found, using fallback:', key);
	return new MeshStandardMaterial({
		color: 0xf0f0f0,
		roughness: 0.8,
		metalness: 0.1
	});
};
